import type { BadgeStateKey } from "../../theme/mantine-theme";

/**
 * Status string (as the API sends it) -> the badge token that renders it.
 *
 * StateBadge deliberately refuses a colour, so a screen showing a status has
 * to come through here. Which means this file is the one place the whole app
 * decides what "REJECTED" looks like, and the one place to look when it needs
 * to change.
 *
 * The unions below MIRROR src/lib/constants.ts. The SPA does not share that
 * module - it is compiled under a different tsconfig with the Express tree as
 * its rootDir - so the mirror is enforced from the other side instead:
 * test/ui-state-map.test.ts reads this file and fails if the server grows a
 * status that has no token here. Without that guard a new status would render
 * as a crash or, worse, as nothing at all.
 */

type ServiceJobStatus =
  | "COLLECTED" | "RECEIVED" | "DIAGNOSED" | "SERVICED"
  | "REPLACED" | "REJECTED" | "DELIVERED" | "CANCELLED";
type DeliveryStatus = "PENDING" | "ASSIGNED" | "DELIVERED" | "FAILED";
type OrderStatus =
  | "CREATED" | "READY_TO_BILL" | "BILLED"
  | "OUT_FOR_DELIVERY" | "DELIVERED" | "CANCELLED";
type PreOrderStatus = "PENDING" | "PARTIALLY_FULFILLED" | "CONVERTED" | "CANCELLED";

/**
 * Typed as a total Record on purpose: the compiler, not a reviewer, is what
 * catches a status added here without a token.
 */
const SERVICE_JOB: Record<ServiceJobStatus, BadgeStateKey> = {
  COLLECTED: "svcCollected",
  RECEIVED: "svcReceived",
  DIAGNOSED: "svcDiagnosed",
  SERVICED: "svcServiced",
  REPLACED: "svcReplaced",
  REJECTED: "svcRejected",
  DELIVERED: "svcDelivered",
  CANCELLED: "svcCancelled",
};

const DELIVERY: Record<DeliveryStatus, BadgeStateKey> = {
  PENDING: "dlvPending",
  ASSIGNED: "dlvAssigned",
  DELIVERED: "dlvDelivered",
  FAILED: "dlvFailed",
};

const ORDER: Record<OrderStatus, BadgeStateKey> = {
  CREATED: "ordCreated",
  READY_TO_BILL: "ordReadyToBill",
  BILLED: "ordBilled",
  OUT_FOR_DELIVERY: "ordOutForDelivery",
  DELIVERED: "ordDelivered",
  CANCELLED: "ordCancelled",
};

const PRE_ORDER: Record<PreOrderStatus, BadgeStateKey> = {
  PENDING: "preOrderPending",
  PARTIALLY_FULFILLED: "preOrderPartial",
  CONVERTED: "preOrderConverted",
  CANCELLED: "preOrderCancelled",
};

/**
 * The API types these as plain strings, so every lookup can miss at runtime
 * even though the maps above are total. Returning undefined rather than
 * throwing lets a screen fall back to showing the raw status - an unstyled
 * truth beats a blank space or a crashed panel.
 */
const lookup =
  <K extends string>(table: Record<K, BadgeStateKey>) =>
  (status: string | null | undefined): BadgeStateKey | undefined =>
    status ? table[status as K] : undefined;

export const serviceJobState = lookup(SERVICE_JOB);
export const deliveryState = lookup(DELIVERY);
export const orderState = lookup(ORDER);
export const preOrderState = lookup(PRE_ORDER);
